import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Helper to format API error details
function formatApiErrorDetail(detail) {
  if (detail == null) return null;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Setup axios interceptor for automatic token refresh
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

// Attach session token from localStorage as Authorization header (mobile Safari cookie fallback)
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('sif_session_token');
  if (token && !config.headers.Authorization) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Skip refresh for auth endpoints themselves
    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !originalRequest.url?.includes('/auth/login') &&
      !originalRequest.url?.includes('/auth/register') &&
      !originalRequest.url?.includes('/auth/refresh') &&
      !originalRequest.url?.includes('/auth/google/session')
    ) {
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then(() => axios(originalRequest));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await axios.post(`${API_URL}/api/auth/refresh`, {}, { withCredentials: true });
        processQueue(null);
        return axios(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError);
        return Promise.reject(error);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

export function AuthProvider({ children }) {
  // null = checking, false = not authenticated, object = authenticated
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [planInfo, setPlanInfo] = useState(null);

  const fetchPlanInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/api/user/plan`, { withCredentials: true });
      setPlanInfo(response.data);
    } catch {
      setPlanInfo(null);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
      localStorage.setItem('sif_auth', '1');
    } catch (error) {
      // If we previously had a session, try one more time with a small delay
      // (handles race conditions with cookie storage on mobile)
      const hadSession = localStorage.getItem('sif_auth');
      if (hadSession && !error.config?._authRetried) {
        try {
          await new Promise(r => setTimeout(r, 500));
          const retryResponse = await axios.get(`${API_URL}/api/auth/me`, {
            withCredentials: true,
            _authRetried: true
          });
          setUser(retryResponse.data);
          setLoading(false);
          return;
        } catch {}
      }
      setUser(false);
      localStorage.removeItem('sif_auth');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  useEffect(() => {
    if (user && user !== false) {
      fetchPlanInfo();
    } else {
      setPlanInfo(null);
    }
  }, [user, fetchPlanInfo]);

  const login = async (email, password) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/login`,
        { email, password },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.access_token) localStorage.setItem('sif_session_token', data.access_token);
      setUser(data);
      localStorage.setItem('sif_auth', '1');
      return { success: true, user: data };
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail);
      const message = detail || (error.response ? `Server error (${error.response.status})` : 'Unable to connect. Please check your internet connection.');
      return { success: false, error: message };
    }
  };

  const register = async (name, email, password, profession) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        { name, email, password, profession },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.access_token) localStorage.setItem('sif_session_token', data.access_token);
      setUser(data);
      localStorage.setItem('sif_auth', '1');
      return { success: true, user: data };
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail);
      const message = detail || (error.response ? `Server error (${error.response.status})` : 'Unable to connect. Please check your internet connection.');
      return { success: false, error: message };
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      console.error('Logout error:', error);
    }
    setUser(false);
    localStorage.removeItem('sif_auth');
    localStorage.removeItem('sif_session_token');
    localStorage.removeItem('sif_google_session');
  };

  const loginWithGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const processGoogleSession = async (sessionId) => {
    try {
      const response = await axios.post(
        `${API_URL}/api/auth/google/session`,
        { session_id: sessionId },
        { withCredentials: true }
      );
      const data = response.data;
      // Store tokens in localStorage as cookie fallback (mobile Safari ITP blocks XHR cookies)
      if (data.access_token) localStorage.setItem('sif_session_token', data.access_token);
      if (data.session_token) localStorage.setItem('sif_google_session', data.session_token);
      setUser(data);
      localStorage.setItem('sif_auth', '1');
      return { success: true, user: data };
    } catch (error) {
      const detail = formatApiErrorDetail(error.response?.data?.detail);
      const message = detail || (error.response ? `Server error (${error.response.status})` : 'Unable to connect. Please check your internet connection.');
      return { success: false, error: message };
    }
  };

  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
      setUser(response.data);
    } catch {}
  };

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    loginWithGoogle,
    processGoogleSession,
    checkAuth,
    refreshUser,
    planInfo,
    fetchPlanInfo,
    isAuthenticated: !!user && user !== false
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export default AuthContext;
